/**
 * InvoiceDetailsView Component - Full-page view for invoice details
 */

import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  ArrowBack,
  Download,
  Send,
  Payment,
  Schedule,
  Edit,
  Cancel,
  ContentCopy,
} from "@mui/icons-material";
import { format } from "date-fns";
import AddPaymentDialog from "./AddPaymentDialog.jsx";
import {
  getInvoice,
  markInvoiceAsSent,
  cancelInvoice,
  reviseInvoice,
} from "../services/invoiceService.js";
import { invoiceDownloadService } from "../services/downloadService.js";
import FollowUpMention from "../../followups/components/FollowUpMention.jsx";
import { useItems } from "../../../hooks/useItems.js";
import CustomerNotesPanel from "../../customers/components/CustomerNotesPanel.jsx";
import { ConfigProvider } from "antd";
import { bubakiiTheme } from "../../../theme/bubakiiTheme.js";
import RefundForm from "./RefundForm.jsx";
import RefundList from "./RefundList.jsx";
import SendInvoiceModal from "./SendInvoiceModal.jsx";
import { getInvoiceDisplayStatus } from "../utils/getInvoiceDisplayStatus.js";
import { useInvoiceLedger } from "../../ledger/hooks/useLedger.js";
import LedgerTimeline from "../../ledger/components/LedgerTimeline.jsx";
import { PageShell } from "@/shared/components/pageShell";
import {
  JobJourneyStrip,
  seedFromInvoice,
  useJobJourneyLinks,
} from "../../../shared/jobJourney/index.js";

/**
 * InvoiceDetailsView Component
 */
const InvoiceDetailsView = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Get initial invoice data from location state (optimistic update)
  const initialInvoice = location.state?.invoice;

  const [invoice, setInvoice] = useState(initialInvoice || null);
  const [loading, setLoading] = useState(!initialInvoice); // Don't show loading if we have initial data
  const [error, setError] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelData, setCancelData] = useState({ reason: "" });
  const [reviseDialog, setReviseDialog] = useState(false);
  const [reviseData, setReviseData] = useState({
    reason: "",
    cancelOriginal: false,
  });
  const [refundDialog, setRefundDialog] = useState(false);
  const [sendInvoiceModalOpen, setSendInvoiceModalOpen] = useState(false);

  const jobJourneyLinks = useJobJourneyLinks(
    invoice ? seedFromInvoice(invoice) : { current: "invoice", enabled: false }
  );

  // Memoize asOfDate to prevent infinite re-renders (new Date() creates new object each render)
  const asOfDate = useMemo(() => new Date(), []);

  // Load ledger entries for this invoice
  const { data: ledgerData, isLoading: ledgerLoading } = useInvoiceLedger(invoiceId, {
    asOfDate,
  });
  const ledgerEntries = ledgerData?.success ? ledgerData.data : [];

  // Fetch items separately using itemSetId (if available)
  // Note: Backend populates items automatically, but this hook provides fallback
  const { items: fetchedItems } = useItems(
    invoice?.itemSetId,
    invoice?.items || [] // Fallback to invoice.items for backward compatibility
  );

  useEffect(() => {
    if (invoiceId) {
      loadInvoice();
    }
  }, [invoiceId]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const result = await getInvoice(invoiceId);
      if (result.success) {
        setInvoice(result.data);
      } else {
        setError(result.error || "Failed to load invoice");
      }
    } catch (err) {
      setError(err.message || "Failed to load invoice");
    } finally {
      setLoading(false);
    }
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      await invoiceDownloadService.downloadPdf(invoiceId);
    } catch (err) {
      setError("Failed to download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleMarkAsSent = () => {
    setSendInvoiceModalOpen(true);
  };

  const handleFollowUpNavigate = () => {
    const entityId = invoice?.id || invoiceId;
    if (!entityId) return;
    const followUpId =
      invoice?.followUpSummary?.nextFollowUpId ||
      invoice?.followUpSummary?.latestFollowUpId;
    if (followUpId) {
      navigate(`/followups/${followUpId}`);
    } else {
      navigate(`/followups?entityType=invoice&entityId=${entityId}`);
    }
  };

  const handleCancelInvoice = async () => {
    try {
      setProcessing(true);
      const result = await cancelInvoice(invoiceId, {
        reason: cancelData.reason,
      });
      if (result.success) {
        setCancelDialog(false);
        setCancelData({ reason: "" });
        await loadInvoice();
      } else {
        setError(result.error || "Failed to cancel invoice");
      }
    } catch (err) {
      setError(err.message || "Failed to cancel invoice");
    } finally {
      setProcessing(false);
    }
  };

  const handleReviseInvoice = async () => {
    try {
      setProcessing(true);
      const result = await reviseInvoice(invoiceId, {
        updates: {},
        cancelOriginal: reviseData.cancelOriginal,
        reason: reviseData.reason,
      });
      if (result.success) {
        setReviseDialog(false);
        setReviseData({ reason: "", cancelOriginal: false });
        const newInvoiceId = result.data?.newInvoiceId || result.newInvoiceId;
        if (newInvoiceId) {
          navigate(`/invoices/${newInvoiceId}`);
        } else {
          await loadInvoice();
        }
      } else {
        setError(result.error || "Failed to revise invoice");
      }
    } catch (err) {
      setError(err.message || "Failed to revise invoice");
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  // Page structure shows immediately - show loading/error states inline

  const paymentSummary = invoice?.paymentSummary || {
    totalPaid: 0,
    balance: invoice?.total || 0,
    isPaid: false,
    isPartial: false,
  };

  // Get display status (prioritizes payment summary over stored status)
  const displayStatus = invoice ? getInvoiceDisplayStatus(invoice) : null;

  return (
    <PageShell sx={{ maxWidth: 1200, mx: "auto" }}>
      {/* Error state - show inline */}
      {error && !invoice && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading state - show inline */}
      {loading && !invoice && (
        <Box
          sx={{
            textAlign: "center",
            p: 4,
            mb: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography>Loading invoice...</Typography>
        </Box>
      )}

      {/* Not found state - show inline */}
      {!loading && !error && !invoice && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Invoice not found
          </Alert>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate("/invoices")}
            variant="outlined"
          >
            Back to Invoices
          </Button>
        </Box>
      )}

      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/invoices")}
          variant="outlined"
        >
          Back to Invoices
        </Button>
        <Typography variant="h4">Invoice Details</Typography>
        <FollowUpMention
          summary={invoice?.followUpSummary}
          onClick={handleFollowUpNavigate}
          size="medium"
          variant="filled"
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          startIcon={downloadingPdf ? <CircularProgress size={16} /> : <Download />}
          onClick={handleDownloadPdf}
          variant="outlined"
          disabled={downloadingPdf}
        >
          Download PDF
        </Button>
        {invoice &&
          (invoice.status === "draft" || invoice.status === "pending") && (
            <Button
              startIcon={<Edit />}
              onClick={() => navigate(`/invoices/${invoiceId}/edit`)}
              variant="outlined"
            >
              Edit
            </Button>
          )}
        {invoice &&
          displayStatus !== "sent" &&
          displayStatus !== "paid" &&
          displayStatus !== "cancelled" && (
            <Button
              startIcon={<Send />}
              onClick={handleMarkAsSent}
              variant="contained"
              disabled={processing}
            >
              Send Invoice
            </Button>
          )}
        {invoice && !paymentSummary.isPaid && (
          <Button
            startIcon={<Payment />}
            onClick={() => setPaymentDialog(true)}
            variant="contained"
          >
            Add Payment
          </Button>
        )}
        {invoice &&
          paymentSummary.totalPaid > 0 &&
          displayStatus !== "cancelled" && (
            <Button
              startIcon={<Payment />}
              onClick={() => setRefundDialog(true)}
              variant="outlined"
              color="warning"
            >
              Create Refund
            </Button>
          )}
        {invoice &&
          displayStatus !== "cancelled" &&
          displayStatus !== "paid" && (
            <Button
              startIcon={<Cancel />}
              onClick={() => setCancelDialog(true)}
              variant="outlined"
              color="error"
            >
              Cancel Invoice
            </Button>
          )}
        {invoice && displayStatus !== "cancelled" && (
          <Button
            startIcon={<ContentCopy />}
            onClick={() => setReviseDialog(true)}
            variant="outlined"
          >
            Revise Invoice
          </Button>
        )}
        <Button
          startIcon={<Schedule />}
          onClick={() =>
            navigate(`/followups/new?entityType=invoice&entityId=${invoiceId}`)
          }
          variant="outlined"
        >
          Create Follow-up
        </Button>
      </Box>

      {invoice ? (
        <JobJourneyStrip
          current="invoice"
          customerId={jobJourneyLinks.customerId}
          leadId={jobJourneyLinks.leadId}
          quoteId={jobJourneyLinks.quoteId}
          invoiceId={jobJourneyLinks.invoiceId}
          orderId={jobJourneyLinks.orderId}
          customerDisplay={jobJourneyLinks.customerDisplay}
          leadDisplay={jobJourneyLinks.leadDisplay}
          quoteDisplay={jobJourneyLinks.quoteDisplay}
          invoiceDisplay={jobJourneyLinks.invoiceDisplay}
          orderDisplay={jobJourneyLinks.orderDisplay}
        />
      ) : null}

      {/* Show inline error if invoice exists but there's an error */}
      {error && invoice && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Show content only when invoice is loaded */}
      {invoice ? (
        <Grid container spacing={3}>
          {/* Invoice Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Invoice Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Invoice ID
                    </Typography>
                    <Typography variant="body1">{invoice.id}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={displayStatus}
                      color={
                        displayStatus === "paid"
                          ? "success"
                          : displayStatus === "partial"
                            ? "warning"
                            : displayStatus === "overdue"
                              ? "error"
                              : displayStatus === "cancelled"
                                ? "error"
                                : "default"
                      }
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(invoice.createdAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Due Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(invoice.dueDate)}
                    </Typography>
                  </Grid>
                  {invoice.sentAt && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Sent At
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(invoice.sentAt)}
                      </Typography>
                    </Grid>
                  )}
                  {invoice.quoteId && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Quote ID
                      </Typography>
                      <Typography variant="body1">{invoice.quoteId}</Typography>
                    </Grid>
                  )}
                  {invoice.revisedFrom && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Revised From
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ cursor: "pointer", color: "primary.main" }}
                        onClick={() =>
                          navigate(`/invoices/${invoice.revisedFrom}`)
                        }
                      >
                        {invoice.revisedFrom}
                      </Typography>
                    </Grid>
                  )}
                  {invoice.revisedTo && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Revised To
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ cursor: "pointer", color: "primary.main" }}
                        onClick={() =>
                          navigate(`/invoices/${invoice.revisedTo}`)
                        }
                      >
                        {invoice.revisedTo}
                      </Typography>
                    </Grid>
                  )}
                  {invoice.revisionNumber && invoice.revisionNumber > 1 && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Revision Number
                      </Typography>
                      <Typography variant="body1">
                        {invoice.revisionNumber}
                      </Typography>
                    </Grid>
                  )}
                  {invoice.cancelledAt && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Cancelled At
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(invoice.cancelledAt)}
                        </Typography>
                      </Grid>
                      {invoice.cancellationReason && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            Cancellation Reason
                          </Typography>
                          <Typography variant="body1">
                            {invoice.cancellationReason}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Customer Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {invoice.customerName || "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Phone
                </Typography>
                <Typography variant="body1">
                  {invoice.customerPhone || "N/A"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Items */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Invoice Items
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Description</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Unit Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(fetchedItems.length > 0
                        ? fetchedItems
                        : invoice?.items || []
                      ).map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.description}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>
                            {formatCurrency(item.unitPrice)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.total)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Pricing Summary */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Pricing Summary
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography>Subtotal:</Typography>
                  <Typography>{formatCurrency(invoice.subtotal)}</Typography>
                </Box>
                {invoice.tax > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography>Tax ({invoice.taxRate}%):</Typography>
                    <Typography>{formatCurrency(invoice.tax)}</Typography>
                  </Box>
                )}
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6">
                    {formatCurrency(invoice.total)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography>Total Paid:</Typography>
                  <Typography color="success.main">
                    {formatCurrency(paymentSummary.totalPaid)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="h6">Balance:</Typography>
                  <Typography
                    variant="h6"
                    color={
                      paymentSummary.balance > 0 ? "error.main" : "success.main"
                    }
                  >
                    {formatCurrency(paymentSummary.balance)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Payment History */}
          {invoice.payments && invoice.payments.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Payment History
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {invoice.payments.map((payment, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body1">
                          {formatCurrency(payment.amount)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(payment.date)}
                        </Typography>
                      </Box>
                      {payment.reference && (
                        <Typography variant="body2" color="text.secondary">
                          Reference: {payment.reference}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        Method: {payment.method}
                      </Typography>
                      {payment.notes && (
                        <Typography variant="body2" color="text.secondary">
                          Notes: {payment.notes}
                        </Typography>
                      )}
                      {index < invoice.payments.length - 1 && (
                        <Divider sx={{ mt: 2 }} />
                      )}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Refunds */}
          {invoice.refunds && invoice.refunds.length > 0 && (
            <Grid item xs={12}>
              <RefundList invoice={invoice} onRefundUpdated={loadInvoice} />
            </Grid>
          )}

          {/* Notes */}
          {invoice.notes && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Notes
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography>{invoice.notes}</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Ledger Timeline */}
          {invoiceId && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Financial Ledger</Typography>
                    <Button
                      size="small"
                      onClick={() => navigate(`/finance/invoices/${invoiceId}/ledger`)}
                    >
                      View Full Ledger
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <LedgerTimeline
                    entries={ledgerEntries}
                    loading={ledgerLoading}
                    error={null}
                    title={null}
                  />
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Customer Notes */}
          {invoice?.customerId && (
            <Grid item xs={12}>
              <ConfigProvider theme={bubakiiTheme}>
                <CustomerNotesPanel
                  customerId={invoice.customerId}
                  context="invoice"
                  relatedId={invoice.id}
                  entityType="invoice"
                />
              </ConfigProvider>
            </Grid>
          )}
        </Grid>
      ) : null}

      {/* Payment Dialog */}
      <AddPaymentDialog
        open={paymentDialog}
        onClose={() => setPaymentDialog(false)}
        invoiceId={invoiceId}
        onSaved={async () => {
          setPaymentDialog(false);
          await loadInvoice();
        }}
      />

      {/* Cancel Invoice Dialog */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)}>
        <DialogTitle>Cancel Invoice</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to cancel this invoice? This action cannot be
            undone.
          </Alert>
          {paymentSummary.totalPaid > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Warning:</strong> This invoice has payments totaling{" "}
              {formatCurrency(paymentSummary.totalPaid)}. You may need to
              process a refund.
            </Typography>
          )}
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="Reason for Cancellation"
            value={cancelData.reason}
            onChange={(e) =>
              setCancelData({ ...cancelData, reason: e.target.value })
            }
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCancelInvoice}
            variant="contained"
            color="error"
            disabled={processing}
          >
            {processing ? <CircularProgress size={20} /> : "Cancel Invoice"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revise Invoice Dialog */}
      <Dialog open={reviseDialog} onClose={() => setReviseDialog(false)}>
        <DialogTitle>Revise Invoice</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will create a new invoice based on the current one. You can
            modify the new invoice after creation.
          </Alert>
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="Reason for Revision"
            value={reviseData.reason}
            onChange={(e) =>
              setReviseData({ ...reviseData, reason: e.target.value })
            }
            multiline
            rows={3}
          />
          <TextField
            fullWidth
            select
            sx={{ mt: 2 }}
            label="Cancel Original Invoice?"
            value={reviseData.cancelOriginal ? "yes" : "no"}
            onChange={(e) =>
              setReviseData({
                ...reviseData,
                cancelOriginal: e.target.value === "yes",
              })
            }
          >
            <MenuItem value="no">No, keep original</MenuItem>
            <MenuItem value="yes">Yes, cancel original</MenuItem>
          </TextField>
          {paymentSummary.totalPaid > 0 && reviseData.cancelOriginal && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>Warning:</strong> This invoice has payments totaling{" "}
              {formatCurrency(paymentSummary.totalPaid)}. You may need to
              process a refund before cancelling.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviseDialog(false)}>Cancel</Button>
          <Button
            onClick={handleReviseInvoice}
            variant="contained"
            disabled={processing}
          >
            {processing ? <CircularProgress size={20} /> : "Revise Invoice"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund Form Dialog */}
      <RefundForm
        open={refundDialog}
        onClose={() => setRefundDialog(false)}
        invoice={invoice}
        onRefundCreated={loadInvoice}
      />
      <SendInvoiceModal
        open={sendInvoiceModalOpen}
        onClose={() => setSendInvoiceModalOpen(false)}
        invoice={invoice}
        onSuccess={async (data) => {
          await loadInvoice(); // Refresh invoice data
        }}
      />
    </PageShell>
  );
};

export default InvoiceDetailsView;
